import { Err, Ok, Result } from 'ts-res';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { KameleoonStorageKey } from './constants';
import { IExternalStorage, IStorage, StorageDataType } from './types';
import { IntegrityChecker } from './integrityChecker';
import { KameleoonLogger } from 'src/logging';

// --- Note ---
// Named `DataStorage` to avoid collision with `Storage` from `window` API
export class DataStorage<T extends StorageDataType> implements IStorage<T> {
  private externalStorage: IExternalStorage;
  private storageKey: KameleoonStorageKey;

  constructor(
    externalStorage: IExternalStorage,
    storageKey: KameleoonStorageKey,
  ) {
    this.externalStorage = externalStorage;
    this.storageKey = storageKey;

    const integrityChecker = new IntegrityChecker(storageKey);

    const result = this.read();

    if (!result.ok) {
      if (result.error.type === KameleoonException.StorageEmpty) {
        const defaultData = integrityChecker.getDefaultData();
        this.write(defaultData as T);
      }

      return;
    }

    const data = result.data;

    if (!integrityChecker.isDataValid(data)) {
      const defaultData = integrityChecker.getDefaultData();
      this.write(defaultData as T);
    }
  }

  public read(): Result<T, KameleoonError> {
    try {
      const result = this.externalStorage.read(this.storageKey);

      if (result === null) {
        return Err(new KameleoonError(KameleoonException.StorageEmpty));
      }

      // --- Note ---
      // Have to cast the type here to tell TS that `result.data` type  is a subset of `T` as we check it already in the default generic `StorageDataType` for `IExternalStorage`
      return Ok(result as T);
    } catch (_) {
      KameleoonLogger.error`Failed to read data from storage: ${this.storageKey}`;
      return Err(
        new KameleoonError(KameleoonException.StorageRead, this.storageKey),
      );
    }
  }

  public write(data: T): Result<void, KameleoonError> {
    try {
      this.externalStorage.write(this.storageKey, data);

      return Ok();
    } catch (_) {
      KameleoonLogger.error`Failed to write data to storage: ${this.storageKey}`;
      return Err(
        new KameleoonError(KameleoonException.StorageWrite, this.storageKey),
      );
    }
  }

  public update(data: Partial<T>): Result<void, KameleoonError> {
    const result = this.read();

    if (!result.ok) {
      if (result.error.type === KameleoonException.StorageEmpty) {
        return this.write(data as T);
      } else {
        return result;
      }
    }

    const existingData: StorageDataType | null = result.data;

    let newData: T;

    if (typeof existingData === 'string') {
      newData = data as T;
    } else {
      newData = { ...existingData, ...data } as T;
    }

    return this.write(newData);
  }
}
